import { z } from "zod";

// Strict for known fields, flexible for unknown
export const dustActionSchema = z
  .object({
    id: z.string().optional(),
    type: z.string().optional(),
    actionType: z.string().optional(),
    internalMCPServerName: z.string().optional(),
    functionCallName: z.string().optional(),
    params: z.record(z.unknown()).optional(),
    query: z.string().optional(),
    url: z.string().url().optional(),
    appName: z.string().optional(),
    name: z.string().optional(),
    documents: z.array(z.unknown()).optional(),
    output: z
      .object({
        results: z.array(z.unknown()).optional(),
      })
      .optional(),
    inputs: z.array(z.unknown()).optional(),
    dataSources: z.unknown().optional(),
  })
  .passthrough(); // Allow unknown fields from API

// Dust conversation schema
export const dustConversationSchema = z
  .object({
    sId: z.string(),
    title: z.string().optional(),
    visibility: z.string().optional(),
    content: z.array(z.array(z.unknown())).optional(),
    createdAt: z.number().optional(),
  })
  .passthrough();

// Dust message schema
export const dustMessageSchema = z
  .object({
    sId: z.string(),
    content: z.string(),
    created: z.number(),
    type: z.enum(["user_message", "agent_message"]).optional(),
    configuration: z
      .object({
        sId: z.string(),
      })
      .passthrough()
      .optional(),
    chainOfThought: z.string().optional(),
    actions: z.array(dustActionSchema).optional(),
  })
  .passthrough();

// Discriminated union for message types (strict)
export const dustMessageItemSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("user_message"),
      sId: z.string(),
      content: z.string(),
      created: z.number(),
    })
    .passthrough(),
  z
    .object({
      type: z.literal("agent_message"),
      sId: z.string(),
      content: z.string(),
      created: z.number(),
      /** Duration of message generation in milliseconds (from Dust API). */
      completionDurationMs: z.number().optional(),
      /** Unix ms when generation finished (Dust API); prefer over created for display. */
      completedTs: z.number().nullable().optional(),
      status: z
        .enum(["created", "succeeded", "failed", "cancelled"])
        .optional(),
      error: z
        .object({
          code: z.string(),
          message: z.string(),
        })
        .passthrough()
        .nullable()
        .optional(),
      chainOfThought: z.string().optional(),
      actions: z.array(dustActionSchema).optional(),
      configuration: z
        .object({
          sId: z.string(),
        })
        .passthrough()
        .optional(),
    })
    .passthrough(),
]);

// Dust agent event schema
export const dustAgentEventSchema = z
  .object({
    type: z.string(),
    classification: z.enum(["chain_of_thought", "tokens"]).optional(),
    text: z.string().optional(),
    action: dustActionSchema.optional(),
    message: z
      .object({
        content: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

// Complete ApiChunk union (remove catch-all)
// Note: offset and streamId are optional for backward compatibility with old streams
export const apiChunkSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("conversation_created"),
      conversation: dustConversationSchema,
      message: dustMessageSchema,
      offset: z.string().optional(), // ULID offset for resumption
      streamId: z.string().optional(), // Stream path (conversationId/messageId)
    }),
    z.object({
      type: z.literal("conversation_context"),
      conversation: dustConversationSchema,
      offset: z.string().optional(),
      streamId: z.string().optional(),
    }),
    z.object({
      type: z.literal("user_message_sent"),
      message: dustMessageSchema,
      offset: z.string().optional(),
      streamId: z.string().optional(),
    }),
    z.object({
      type: z.literal("agent_event"),
      event: dustAgentEventSchema,
      offset: z.string().optional(),
      streamId: z.string().optional(),
    }),
    z.object({
      type: z.literal("stream_complete"),
      offset: z.string().optional(),
      streamId: z.string().optional(),
    }),
    z.object({
      type: z.literal("stream_error"),
      error: z.string(),
      offset: z.string().optional(),
      streamId: z.string().optional(),
    }),
  ])
  .or(
    z
      .object({
        type: z.string(),
        conversationId: z.string().optional(),
        conversation: dustConversationSchema.optional(),
        message: dustMessageSchema.optional(),
        event: dustAgentEventSchema.optional(),
        offset: z.string().optional(),
        streamId: z.string().optional(),
      })
      .passthrough()
  ); // Fallback for unknown events

// Inferred types
export type DustAction = z.infer<typeof dustActionSchema>;
export type DustConversation = z.infer<typeof dustConversationSchema>;
export type DustMessage = z.infer<typeof dustMessageSchema>;
export type DustMessageItem = z.infer<typeof dustMessageItemSchema>;
export type DustAgentEvent = z.infer<typeof dustAgentEventSchema>;
export type ApiChunk = z.infer<typeof apiChunkSchema>;
